import type { MenuProps } from "antd";
import { Dropdown, DatePicker } from "antd";
import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useIntl } from "react-intl";

const { RangePicker } = DatePicker;

type Props = {
  value: [string, string];
  onChange?: (value: [Dayjs, Dayjs]) => void;
};

export default function MonthlyFilters(props: Props) {
  const { value, onChange } = props;
  const { messages } = useIntl();
  const [selectedFilter, setSelectedFilter] = useState<React.Key>();
  const [isOpen, setIsOpen] = useState(false);

  const getCurrentWeekRange = (): [dayjs.Dayjs, dayjs.Dayjs] => {
    const start = dayjs().startOf("week");
    const end = dayjs().endOf("week");
    return [start, end];
  };

  const getCurrentMonthRange = (): [dayjs.Dayjs, dayjs.Dayjs] => {
    const start = dayjs().startOf("month");
    const end = dayjs().endOf("month");
    return [start, end];
  };

  const getLastMonthRange = (): [dayjs.Dayjs, dayjs.Dayjs] => {
    const start = dayjs().subtract(1, "month").startOf("month");
    const end = dayjs().subtract(1, "month").endOf("month");
    return [start, end];
  };

  const formatDateRange = (start: dayjs.Dayjs, end: dayjs.Dayjs): string => {
    const format = "D MMM";
    return `${start.format(format)} - ${end.format(format)}`;
  };

  useEffect(() => {
    const currentMonth = getCurrentMonthRange();
    const lastMonth = getLastMonthRange();
    if (
      dayjs(value[0]).isSame(currentMonth[0], "day") &&
      dayjs(value[1]).isSame(currentMonth[1], "day")
    ) {
      setSelectedFilter("currentMonth");
    } else if (
      dayjs(value[0]).isSame(lastMonth[0], "day") &&
      dayjs(value[1]).isSame(lastMonth[1], "day")
    ) {
      setSelectedFilter("lastMonth");
    } else {
      setSelectedFilter("dateRange");
    }
  }, [value]);

  const items: MenuProps["items"] = [
    {
      key: "currentWeek",
      label: messages["common.thisWeek"] as string,
      onClick: () => {
        const dates = getCurrentWeekRange();
        setSelectedFilter("currentWeek");
        onChange(dates);
      },
    },
    {
      key: "currentMonth",
      label: messages["common.thisMonth"] as string,
      onClick: () => {
        const dates = getCurrentMonthRange();
        setSelectedFilter("currentMonth");
        onChange(dates);
      },
    },
    {
      key: "lastMonth",
      label: messages["common.lastMonth"] as string,
      onClick: () => {
        const dates = getLastMonthRange();
        setSelectedFilter("lastMonth");
        onChange(dates);
      },
    },
    {
      key: "custom",
      label: messages["common.customDate"] as string,
      children: [
        {
          key: "dateRange",
          label: (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={() => setIsOpen(true)}
              onMouseLeave={() => setIsOpen(false)}
            >
              <RangePicker
                value={[dayjs(value[0]), dayjs(value[1])]}
                onChange={(dates) => {
                  if (dates) {
                    setSelectedFilter("dateRange");
                    onChange([dates[0], dates[1]]);
                  }
                }}
                style={{ width: "100%" }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ),
        },
      ],
    },
  ];

  const getDisplayText = () => {
    if (!value) return messages["common.dateFilter"] as string;
    if (selectedFilter === "dateRange") {
      return formatDateRange(dayjs(value[0]), dayjs(value[1]));
    }
    return (
      (items?.find((item) => item.key === selectedFilter) as any)?.label ||
      " - "
    );
  };

  return (
    <Dropdown
      menu={{ items: onChange ? items : [] }}
      trigger={["hover"]}
      placement="bottomRight"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className="cursor-pointer hover:bg-gray-100 p-2 rounded">
        <span className="text-gray-600">{getDisplayText()}</span>
      </div>
    </Dropdown>
  );
}
